import {
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
  InputLabel,
  Box,
  Paper,
  Tooltip,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import axios from "axios";
import {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from "react";
import {
  config,
  positionMasterAPI,
  statusMasterAPI,
  departmentMasterAPI,
} from "../config";
import { AlertMessage } from "./AlertMessage";
import { mainMenuList } from "../layout/Sidenav";
import InfoIcon from "@mui/icons-material/Info";

export const AddPosition = forwardRef(({ ...props }, ref) => {
  const [Status, setStatus] = useState(2),
    [positionName, setPositionName] = useState(""),
    [positionID, setPositionID] = useState(null),
    [roleID, setRoleID] = useState(""),
    [roleList, setRoleList] = useState([]),
    [menuID, setMenuID] = useState([]),
    [menuList, setMenuList] = useState([]);
  // Which parent-menu accordions are open, keyed by menu.id. Controlled so
  // it can be collapsed back to the initial (all-closed) state after a
  // save, instead of MUI's Accordion default of remembering its own
  // expanded/collapsed state indefinitely.
  const [expandedMenus, setExpandedMenus] = useState({});
  const [intDepartmentID, setDepartmentname] = useState("");
  const [Department, setDepartment] = useState([]);
  // Invoice access is derived straight from the tree above rather than
  // tracked separately. "Create" is just "Invoice Creation" (201) — no
  // separate toggle, since that's the same permission under a different
  // name. "Edit" (205) has no page of its own, so it keeps its own toggle.
  const canCreateInvoice = menuID.includes(201);
  const canEditInvoice = menuID.includes(205);

  const childRef = useRef();
  const [loading, setLoading] = useState(false);
  const [existingPositions, setExistingPositions] = useState([]);

  useEffect(() => {
    if (!intDepartmentID) {
      setExistingPositions([]);
      return;
    }

    const data = {
      intType: 4,
      intDepartmentID: intDepartmentID,
    };

    config.method = "POST";
    config.data = JSON.stringify(data);
    config.url = positionMasterAPI;

    axios(config)
      .then((response) => {
        if (response.data?.result) {
          setExistingPositions(response.data?.result);
        } else {
          setExistingPositions([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching positions: ", error);
        setExistingPositions([]);
      });
  }, [intDepartmentID]);

  useEffect(() => {
    setMenuList(mainMenuList);
  }, []);

  // A position saved before child ids existed only has the parent id in its
  // CSV (e.g. "1,2,3,7") — that legacy grant means "every child too" (see
  // Sidenav.jsx's own backward-compat filter). Expand it here as well, purely
  // so the checkbox tree DISPLAYS that as fully checked instead of looking
  // like nothing under the module was granted. Parents with at least one
  // child id already present are left alone (an explicit partial grant).
  const expandLegacyGrants = (ids) => {
    const set = new Set(ids);
    mainMenuList.forEach((menu) => {
      if (!menu.childMenuFlag || !menu.childMenu?.length) return;
      if (!set.has(menu.id)) return;
      const hasAnyChild = menu.childMenu.some((c) => set.has(c.id));
      if (!hasAnyChild) {
        menu.childMenu.forEach((c) => set.add(c.id));
      }
    });
    return [...set];
  };

  // Edit access has no page of its own, so it only ever lived as the
  // can_edit_invoice flag until "Edit" (id 205) moved into the tree —
  // migrate that flag in here so an older position still displays/behaves
  // correctly. A position that already has 205 explicitly saved keeps
  // whatever the tree says; only a genuinely legacy row (no 205 yet) gets
  // backfilled from the old flag. (Create needs no such migration — it's
  // just "Invoice Creation", id 201, which already carries its own grant.)
  // Mutates and returns the given Set.
  const mergeLegacyInvoiceToggles = (idSet, data) => {
    if (!idSet.has(205) && idSet.has(2)) {
      const legacyEdit =
        data.can_edit_invoice === undefined
          ? true
          : Boolean(Number(data.can_edit_invoice));
      if (legacyEdit) idSet.add(205);
    }
    return idSet;
  };

  useImperativeHandle(ref, () => ({
    setData(data) {
      const tempData = JSON.parse(data);
      setPositionName(tempData.positionName);
      setPositionID(tempData.intPositionID);
      setStatus(tempData.status);
      setRoleID(tempData.intRoleID);
      setDepartmentname(tempData.intDepartmentID);
      const expandedIds = tempData.intMenuID
        ? expandLegacyGrants(tempData.intMenuID.split(",").map(Number))
        : [];
      const idSet = mergeLegacyInvoiceToggles(new Set(expandedIds), tempData);
      setMenuID([...idSet]);
    },
  }));

  let tempRefs = useRef(false);
  tempRefs.current = false;

  //On Submit
  const handleSubmit = () => {
    setLoading(true);
    const data = JSON.stringify({
      txtPositionName: positionName,
      intPositionStatus: Status,
      intDepartmentID: intDepartmentID,
      intRoleID: roleID,
      intPositionID: positionID,
      intType: 1,
      intMenuID: menuID.join(),
      can_create_invoice: canCreateInvoice ? 1 : 0,
      can_edit_invoice: canEditInvoice ? 1 : 0,
    });

    // 🔒 Prevent duplicate new entries
    const duplicate = existingPositions.find(
      (pos) =>
        pos.position_name.toLowerCase() === positionName.trim().toLowerCase()
    );

    if (duplicate && !positionID) {
      childRef.current.triggerAlert(
        JSON.stringify({
          message:
            "This position already exists. Please select it from the list to edit.",
          messageType: "warning",
        })
      );
      setLoading(false);
      return;
    }

    if ("" == positionName) {
      childRef.current.triggerAlert(
        JSON.stringify({
          message: "Position Name Can't be empty",
          messageType: "error",
        })
      );
      return false;
    } else if (!intDepartmentID) {
      childRef.current.triggerAlert(
        JSON.stringify({
          message: "Department Name Can't be empty",
          messageType: "error",
        })
      );
      return false;
    } else if (!roleID) {
      childRef.current.triggerAlert(
        JSON.stringify({
          message: "Role Can't be empty",
          messageType: "error",
        })
      );
      return false;
    } else if (!menuID || menuID.length === 0) {
      childRef.current.triggerAlert(
        JSON.stringify({
          message: "Menus Can't be empty",
          messageType: "error",
        })
      );
      return false;
    }

    config.method = "POST";
    config.data = data;
    config.url = positionMasterAPI;
    axios(config)
      .then(function (response) {
        childRef.current.triggerAlert(
          JSON.stringify({
            message: response.data.message,
            messageType: response.data.code,
          })
        );

        if (
          response.data.code === "success" ||
          response.data.code === "warning"
        ) {
          props.fetchData(1);
        }

        if (response.data.code === "success") {
          props.fetchData(1);
          // Only clear the form back to "new entry" state after adding a
          // position. An edit (positionID already set) should keep showing
          // what was just saved — clearing menuID here was firing the
          // "Select at least one menu" warning right after a perfectly
          // successful edit.
          if (!positionID) {
            setPositionName("");
            setRoleID(0);
            setDepartmentname(0);
            setStatus(2);
            setMenuID([]);
            setExpandedMenus({});
          }
        }
      })
      .catch(function (error) {
        console.log(error);
        childRef.current.triggerAlert(
          JSON.stringify({
            message: error.data.message || "Something went wrong",
            messageType: "error",
          })
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (tempRefs.current === false) {
      const data = {
        intType: 3,
      };

      config.method = "POST";
      config.data = JSON.stringify(data);
      config.url = departmentMasterAPI;

      axios(config)
        .then(function (response) {
          const activeDepartments = response.data.result.filter(
            (dept) => dept.status === 2
          );
          setDepartment(activeDepartments);
        })
        .catch(function (error) {
          console.log(error);
        });
      return () => {
        tempRefs.current = true;
      };
    }
  }, []);

  useEffect(() => {
    if (tempRefs.current === false) {
      const data = {
        intType: 2,
        intParentId: 33,
      };

      config.method = "POST";
      config.data = JSON.stringify(data);
      config.url = statusMasterAPI;

      axios(config)
        .then(function (response) {
          const activeRoles = response.data.result.filter(
            (role) => role.status === 1
          );
          setRoleList(activeRoles);
        })
        .catch(function (error) {
          console.log(error);
        });
      return () => {
        tempRefs.current = true;
      };
    }
  }, []);

  // Menus (and everything that depends on them) only make sense once the
  // basic identity of the position is known — either the admin has filled in
  // Department/Position Name/Role on a fresh Add, or we're editing an
  // existing position (positionID is set via setData). Keeps the form from
  // dumping the whole menu tree on screen before there's anything to attach
  // it to.
  const showMenus = Boolean(positionID) || Boolean(
    intDepartmentID && positionName.trim() && roleID
  );

  return (
    <Grid container spacing={2.5} className={`bg-white`}>
      <AlertMessage ref={childRef} />

      <Grid item xs={12}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: "text.secondary",
            mb: 0.5,
          }}
        >
          Position Details
        </Typography>
      </Grid>

      <Grid item xs={12} md={3} lg={3}>
        <FormControl className={`form-control`} fullWidth>
          <InputLabel id="demo-simple-select-label">Department</InputLabel>
          <Select
            labelId="demo-simple-select-helper-label"
            id="demo-simple-select-helper"
            label="Department"
            value={intDepartmentID}
            onChange={(e) => {
              setDepartmentname(e.target.value);
            }}
            name="ddlType"
          >
            {Department.map((row, index) => (
              <MenuItem value={row.id}>{row.department_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={3} lg={3}>
        <FormControl className={`form-control`} fullWidth>
          <Autocomplete
            freeSolo
            options={existingPositions.map((pos) => pos.position_name)}
            value={positionName}
            onChange={(event, newValue) => {
              // If user selects existing position → prefill details
              const selected = existingPositions.find(
                (pos) => pos.position_name === newValue
              );
              if (selected) {
                setPositionName(selected.position_name);
                setPositionID(selected.id);
                setRoleID(selected.role_id || "");
                const menuArray = selected.menus
                  ? expandLegacyGrants(selected.menus.split(",").map(Number))
                  : [];
                const idSet = mergeLegacyInvoiceToggles(
                  new Set(menuArray),
                  selected
                );

                setMenuID([...idSet]);
              } else {
                // New entry mode
                setPositionName(newValue || "");
                setPositionID(null);
                setRoleID("");
                setMenuID([]);
              }
            }}
            onInputChange={(event, newInputValue) => {
              setPositionName(newInputValue);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Position Name" />
            )}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} md={3} lg={3}>
        <FormControl className={`form-control`} fullWidth>
          <InputLabel id="demo-simple-select-label">Role</InputLabel>
          <Select
            labelId="demo-simple-select-helper-label"
            id="demo-simple-select-helper"
            label="Role"
            value={roleID}
            onChange={(e) => {
              setRoleID(e.target.value);
            }}
            name="ddlType"
          >
            {roleList.map((row, index) => (
              <MenuItem value={row.id}>{row.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={3} lg={3}>
        <FormControl className={`form-control`} fullWidth>
          <InputLabel id="demo-simple-select-label">Status</InputLabel>
          <Select
            labelId="demo-simple-select-helper-label"
            id="demo-simple-select-helper"
            label="Status"
            value={Status}
            onChange={(e) => {
              setStatus(e.target.value);
            }}
            name="ddlType"
          >
            <MenuItem value={`2`}>Active</MenuItem>
            <MenuItem value={`3`}>Inactive</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Menu — parent modules AND their individual sub-pages are both
          configurable now. Checking a parent grants every child under it;
          unchecking specific children narrows the grant to just the rest
          (the parent id stays selected as long as at least one child is, so
          the module keeps showing in the sidebar — see Sidenav.jsx). Leaving
          every child checked keeps behaving exactly like the old "whole
          module" grant.

          Hidden until Department/Position Name/Role are filled (or we're
          editing an existing position) — see showMenus above. */}
      {showMenus && (
      <Grid item xs={12} md={12} lg={12}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: "text.secondary",
            }}
          >
            Menus
          </Typography>
          <Tooltip
            title="Select the modules — and individual sub-pages within them — you want to grant access to."
            arrow
          >
            <InfoIcon color="action" fontSize="small" style={{ cursor: "pointer" }} />
          </Tooltip>
          {menuID.length > 0 && (
            <Chip
              label={`${menuID.length} selected`}
              size="small"
              sx={{
                ml: "auto",
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                bgcolor: "primary.50",
                color: "primary.main",
              }}
            />
          )}
        </Box>

        {menuID.length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              mb: 1,
              borderRadius: 1,
              bgcolor: "#fdeded",
              border: "1px solid #f6c6c6",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 16, color: "#c0392b" }} />
            <Typography variant="caption" sx={{ color: "#c0392b", fontWeight: 500 }}>
              Select at least one menu
            </Typography>
          </Box>
        )}

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            borderColor: "#e2e7ee",
          }}
        >
          {menuList.map((menu, idx) => {
            const hasChildren = menu.childMenuFlag && menu.childMenu?.length > 0;
            const isLast = idx === menuList.length - 1;

            if (!hasChildren) {
              // Leaf top-level item (e.g. Dashboard) — a single checkbox.
              return (
                <Box
                  key={menu.id}
                  sx={{
                    px: 2,
                    py: 0.5,
                    borderBottom: isLast ? "none" : "1px solid #eef1f5",
                    transition: "background-color 120ms ease",
                    "&:hover": { bgcolor: "#f7f9fc" },
                  }}
                >
                  <FormControlLabel
                    sx={{ width: "100%", m: 0 }}
                    control={
                      <Checkbox
                        checked={menuID.includes(menu.id)}
                        onChange={(e) =>
                          setMenuID((prev) =>
                            e.target.checked
                              ? [...new Set([...prev, menu.id])]
                              : prev.filter((id) => id !== menu.id)
                          )
                        }
                      />
                    }
                    label={<Typography fontWeight={600}>{menu.title}</Typography>}
                  />
                </Box>
              );
            }

            const childIds = menu.childMenu.map((c) => c.id);
            const checkedChildCount = childIds.filter((id) =>
              menuID.includes(id)
            ).length;
            const allChecked = checkedChildCount === childIds.length;
            const someChecked = checkedChildCount > 0 && !allChecked;

            return (
              <Accordion
                key={menu.id}
                disableGutters
                square
                elevation={0}
                expanded={!!expandedMenus[menu.id]}
                onChange={(e, isExpanded) =>
                  setExpandedMenus((prev) => ({ ...prev, [menu.id]: isExpanded }))
                }
                sx={{
                  border: "none",
                  borderBottom: isLast ? "none" : "1px solid #eef1f5",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { bgcolor: "#fafbfd" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 2,
                    minHeight: 48,
                    "&:hover": { bgcolor: "#f7f9fc" },
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      my: 0.5,
                    },
                  }}
                >
                  <FormControlLabel
                    // Stop the row click from also toggling the accordion's
                    // expand/collapse.
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        checked={allChecked}
                        indeterminate={someChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMenuID((prev) => [
                              ...new Set([...prev, menu.id, ...childIds]),
                            ]);
                          } else {
                            setMenuID((prev) =>
                              prev.filter(
                                (id) => id !== menu.id && !childIds.includes(id)
                              )
                            );
                          }
                        }}
                      />
                    }
                    label={<Typography fontWeight={600}>{menu.title}</Typography>}
                  />
                  {checkedChildCount > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ ml: 1, color: "text.secondary" }}
                    >
                      ({checkedChildCount}/{childIds.length})
                    </Typography>
                  )}
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    pl: 6,
                    pr: 2,
                    py: 1,
                    bgcolor: "#fafbfd",
                    borderTop: "1px solid #eef1f5",
                  }}
                >
                  <FormGroup
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        md: "1fr 1fr 1fr",
                      },
                      columnGap: 2,
                    }}
                  >
                    {menu.childMenu.map((child) => (
                      <FormControlLabel
                        key={child.id}
                        sx={{ m: 0, py: 0.25 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={menuID.includes(child.id)}
                            onChange={(e) => {
                              setMenuID((prev) => {
                                const next = e.target.checked
                                  ? [...new Set([...prev, child.id])]
                                  : prev.filter((id) => id !== child.id);
                                const anyChildChecked = childIds.some((id) =>
                                  next.includes(id)
                                );
                                // Keep the parent id in sync: present whenever
                                // at least one child is granted (so the module
                                // still shows in the sidebar), removed only
                                // when every child has been unchecked.
                                if (anyChildChecked) {
                                  return [...new Set([...next, menu.id])];
                                }
                                return next.filter((id) => id !== menu.id);
                              });
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2">{child.title}</Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Paper>
      </Grid>
      )}

      <Grid item xs={12}>
        <Divider sx={{ my: 0.5 }} />
      </Grid>

      <Grid item xs={12} textAlign="right">
        <Button
          variant="primary"
          className={`btn-primary`}
          onClick={handleSubmit}
          sx={{ px: 4, py: 1 }}
        >
          Submit
        </Button>
      </Grid>
    </Grid>
  );
});
